import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { useNotification } from '@/components/NotificationProvider';
import { usePermissions } from '@/components/PermissionsProvider';
import {
  ChevronLeft, ChevronRight, ChevronDown, Search, Maximize2, Minimize2,
  AlertTriangle, CreditCard, Award, Stethoscope, Plane, Plus, X, Clock, Scissors
} from 'lucide-react';
import { formatDisplayDate, formatDateForInput } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function PersonnelGanttView({ open, onOpenChange }) {
  const notification = useNotification();
  const canEdit = usePermissions().canEdit('personnel_planner');
  const containerRef = useRef(null);
  const ganttAreaRef = useRef(null);
  const navigateTimeRef = useRef(null);
  const wheelDetachRef = useRef(null);
  // Ref callback: attaches the Shift+scroll wheel listener exactly when the Gantt
  // content node mounts (the Radix Sheet portal mounts content after effects run,
  // so a plain useEffect on a ref would see null — a ref callback is reliable).
  const setGanttAreaNode = useCallback((node) => {
    ganttAreaRef.current = node;
    if (wheelDetachRef.current) { wheelDetachRef.current(); wheelDetachRef.current = null; }
    if (node) {
      const el = node.closest('[data-radix-scroll-area-viewport]') || node;
      const onWheel = (e) => { if (e.shiftKey) { e.preventDefault(); navigateTimeRef.current && navigateTimeRef.current(e.deltaY > 0 ? 1 : -1); } };
      el.addEventListener('wheel', onWheel, { passive: false });
      wheelDetachRef.current = () => el.removeEventListener('wheel', onWheel);
    }
  }, []);

  // Data state
  const [personnel, setPersonnel] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [projects, setProjects] = useState([]);

  // View state
  const [timeScale, setTimeScale] = useState('weekly');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all'); // specific project ID filter
  const [onlyAllocated, setOnlyAllocated] = useState(false);
  const [showVisaRow, setShowVisaRow] = useState(true);

  // Slide state
  const [sliding, setSliding] = useState(null);
  const [didSlide, setDidSlide] = useState(false);
  // Resize state
  const [resizing, setResizing] = useState(null);
  const [didResize, setDidResize] = useState(false);

  // Collapsed roles
  const [collapsedRoles, setCollapsedRoles] = useState({});

  // Multi-select for batch allocation
  const [selectedIds, setSelectedIds] = useState([]);

  // Add allocation modal (single or batch)
  const [allocModal, setAllocModal] = useState(null);

  // Conflicts (over-allocation / double-bookings)
  const [conflicts, setConflicts] = useState([]);
  const [showConflictsModal, setShowConflictsModal] = useState(false);
  const [showVisaAlertModal, setShowVisaAlertModal] = useState(false);

  useEffect(() => {
    if (open) fetchData();
  }, [open]);

  const fetchData = async () => {
    try {
      const [personnelRes, allocationsRes, projectsRes, conflictsRes] = await Promise.all([
        axios.get(`${API}/personnel`, { headers: getAuthHeader() }),
        axios.get(`${API}/allocations/personnel`, { headers: getAuthHeader() }),
        axios.get(`${API}/projects`, { headers: getAuthHeader() }),
        axios.get(`${API}/allocations/conflicts`, { headers: getAuthHeader() }).catch(() => ({ data: { personnel_conflicts: [] } })),
      ]);
      setPersonnel(personnelRes.data);
      setAllocations(allocationsRes.data);
      setProjects(projectsRes.data);
      setConflicts(conflictsRes.data?.personnel_conflicts || []);
    } catch (error) {
      notification.error('Failed to load data');
    }
  };

  // Generate timeline based on scale - default 3 months
  const generateTimeline = () => {
    const timeline = [];
    const start = new Date(currentDate);

    if (timeScale === 'daily') {
      start.setDate(start.getDate() - start.getDay());
      for (let i = 0; i < 21; i++) {
        const date = new Date(start);
        date.setDate(start.getDate() + i);
        timeline.push({
          date,
          label: date.getDate().toString(),
          subLabel: date.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', year: '2-digit' }),
          isWeekend: date.getDay() === 0 || date.getDay() === 6
        });
      }
    } else if (timeScale === 'weekly') {
      start.setDate(1);
      const numWeeks = 13;
      for (let i = 0; i < numWeeks; i++) {
        const weekStart = new Date(start);
        weekStart.setDate(start.getDate() + (i * 7));
        timeline.push({
          date: weekStart,
          label: `W${Math.ceil(weekStart.getDate() / 7)}`,
          subLabel: weekStart.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
        });
      }
    } else {
      start.setDate(1);
      for (let i = 0; i < 3; i++) {
        const month = new Date(start);
        month.setMonth(start.getMonth() + i);
        timeline.push({
          date: month,
          label: month.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }),
          subLabel: month.getFullYear().toString()
        });
      }
    }
    return timeline;
  };

  const timeline = generateTimeline();
  const timelineStart = timeline[0]?.date || new Date();
  const timelineEnd = timeline[timeline.length - 1]?.date || new Date();

  const getAdjustedTimelineEnd = () => {
    const adjustedEnd = new Date(timelineEnd);
    if (timeScale === 'daily') adjustedEnd.setDate(adjustedEnd.getDate() + 1);
    else if (timeScale === 'weekly') adjustedEnd.setDate(adjustedEnd.getDate() + 7);
    else adjustedEnd.setMonth(adjustedEnd.getMonth() + 1);
    return adjustedEnd;
  };

  const getBarStyle = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const adjustedEnd = getAdjustedTimelineEnd();

    const totalDays = (adjustedEnd - timelineStart) / (1000 * 60 * 60 * 24);
    const barStart = Math.max(0, (start - timelineStart) / (1000 * 60 * 60 * 24));
    const barEnd = Math.min(totalDays, (end - timelineStart) / (1000 * 60 * 60 * 24));

    if (barEnd < 0 || barStart > totalDays) return null;

    const left = (barStart / totalDays) * 100;
    const width = ((barEnd - barStart) / totalDays) * 100;

    return { left: `${Math.max(0, left)}%`, width: `${Math.min(100 - left, Math.max(1, width))}%` };
  };

  // Derive the "current" visa for a person from visa_history (latest expiry, tiebreaker latest start).
  // Falls back to legacy single-field visa_type/visa_expiry if no history exists.
  const getCurrentVisa = (person) => {
    const history = Array.isArray(person.visa_history) ? person.visa_history : [];
    if (history.length > 0) {
      const sorted = [...history].sort((a, b) => {
        const aExp = a.expiry_date ? new Date(a.expiry_date).getTime() : 0;
        const bExp = b.expiry_date ? new Date(b.expiry_date).getTime() : 0;
        if (aExp !== bExp) return bExp - aExp;
        const aStart = a.start_date ? new Date(a.start_date).getTime() : 0;
        const bStart = b.start_date ? new Date(b.start_date).getTime() : 0;
        return bStart - aStart;
      });
      return sorted[0];
    }
    // Legacy fallback (no history yet but expiry on record)
    if (person.visa_expiry) {
      return { visa_type: person.visa_type || 'Visa', start_date: null, expiry_date: person.visa_expiry, _legacy: true };
    }
    return null;
  };

  // Return ALL visas for a person (each co-existing visa becomes its own Gantt row).
  // Sorted by expiry date ascending (soonest-expiring first). Falls back to the legacy
  // single visa field if no history exists.
  const getAllVisas = (person) => {
    const history = Array.isArray(person.visa_history) ? person.visa_history : [];
    if (history.length > 0) {
      return [...history].sort((a, b) => {
        const aExp = a.expiry_date ? new Date(a.expiry_date).getTime() : Infinity;
        const bExp = b.expiry_date ? new Date(b.expiry_date).getTime() : Infinity;
        return aExp - bExp;
      });
    }
    if (person.visa_expiry) {
      return [{ visa_type: person.visa_type || 'Visa', start_date: null, expiry_date: person.visa_expiry, _legacy: true }];
    }
    return [];
  };
  const getVisaBarColor = (expiryDate) => {
    if (!expiryDate) return 'bg-slate-300';
    const exp = new Date(expiryDate);
    const today = new Date();
    const days = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'bg-red-500';
    if (days <= 60) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  // Parse rotation pattern
  const parseRotation = (rotationStr) => {
    if (!rotationStr) return null;
    const match = rotationStr.match(/(\d+)\s*[:\/-]\s*(\d+)/);
    if (match) {
      return { onWeeks: parseInt(match[1]), offWeeks: parseInt(match[2]) };
    }
    return null;
  };

  // Generate rotation segments for an allocation using next_rotation_start
  const generateRotationSegments = (allocation, person) => {
    const rotation = parseRotation(person.rotation_roster);
    if (!rotation) return [];

    const segments = [];
    const allocStart = new Date(allocation.start_date);
    const allocEnd = new Date(allocation.end_date);
    const adjustedTimelineEnd = getAdjustedTimelineEnd();

    let rosterStart;
    if (person.next_rotation_start) {
      rosterStart = new Date(person.next_rotation_start);
      if (rosterStart >= allocEnd) return [];
    } else {
      rosterStart = new Date(allocStart);
    }

    let currentDate = new Date(rosterStart);
    let swingNumber = 1;
    let isRosteredOn = true;

    while (currentDate < allocEnd) {
      const weeksInPhase = isRosteredOn ? rotation.onWeeks : rotation.offWeeks;
      const phaseEnd = new Date(currentDate);
      phaseEnd.setDate(phaseEnd.getDate() + (weeksInPhase * 7));

      const segmentStart = new Date(Math.max(currentDate.getTime(), allocStart.getTime(), timelineStart.getTime()));
      const segmentEnd = new Date(Math.min(phaseEnd.getTime(), allocEnd.getTime(), adjustedTimelineEnd.getTime()));

      if (segmentStart < segmentEnd && segmentEnd > timelineStart && segmentStart < adjustedTimelineEnd) {
        const style = getBarStyle(segmentStart, segmentEnd);
        if (style) {
          segments.push({
            type: isRosteredOn ? 'rostered_on' : 'r_r',
            start: segmentStart,
            end: segmentEnd,
            style,
            swingNumber: isRosteredOn ? swingNumber : null
          });
        }
      }

      if (isRosteredOn) swingNumber++;
      isRosteredOn = !isRosteredOn;
      currentDate = phaseEnd;
    }

    return segments;
  };

  // Get compliance markers
  const getComplianceMarkers = (person, allocation) => {
    const markers = [];
    const allocStart = new Date(allocation.start_date);
    const allocEnd = new Date(allocation.end_date);

    if (person.visa_expiry) {
      const visaDate = new Date(person.visa_expiry);
      if (visaDate >= allocStart && visaDate <= allocEnd) {
        markers.push({ type: 'visa', date: visaDate, label: 'Visa Expiry', icon: CreditCard, color: 'bg-orange-500' });
      }
    }

    (person.certifications || []).forEach(cert => {
      if (cert.expiry_date) {
        const certDate = new Date(cert.expiry_date);
        if (certDate >= allocStart && certDate <= allocEnd) {
          markers.push({ type: 'cert', date: certDate, label: `${cert.name || cert.type} Expiry`, icon: Award, color: 'bg-red-500' });
        }
      }
    });

    (person.medical_records || []).forEach(med => {
      if (med.expiry_date) {
        const medDate = new Date(med.expiry_date);
        if (medDate >= allocStart && medDate <= allocEnd) {
          markers.push({ type: 'medical', date: medDate, label: `${med.type || 'Medical'} Expiry`, icon: Stethoscope, color: 'bg-purple-500' });
        }
      }
    });

    return markers;
  };

  // Get FIFO flight markers (within visible timeline)
  const getFifoMarkers = (person) => {
    const markers = [];
    const adjustedEnd = getAdjustedTimelineEnd();

    (person.fifo_dates || []).forEach(fifo => {
      if (fifo.fly_in_date) {
        const flyInDate = new Date(fifo.fly_in_date);
        if (flyInDate >= timelineStart && flyInDate <= adjustedEnd) {
          markers.push({
            type: 'fly_in', date: flyInDate,
            label: `Fly In${fifo.fly_in_flight ? ` (${fifo.fly_in_flight})` : ''}`,
            from: fifo.fly_in_from, to: fifo.fly_in_to, time: fifo.fly_in_time,
            icon: Plane, color: 'bg-emerald-600', rotation: '-rotate-45'
          });
        }
      }
      if (fifo.fly_out_date) {
        const flyOutDate = new Date(fifo.fly_out_date);
        if (flyOutDate >= timelineStart && flyOutDate <= adjustedEnd) {
          markers.push({
            type: 'fly_out', date: flyOutDate,
            label: `Fly Out${fifo.fly_out_flight ? ` (${fifo.fly_out_flight})` : ''}`,
            from: fifo.fly_out_from, to: fifo.fly_out_to, time: fifo.fly_out_time,
            icon: Plane, color: 'bg-blue-600', rotation: 'rotate-45'
          });
        }
      }
    });

    return markers;
  };

  // Derive R&R spans from FIFO flights: each Fly Out starts an R&R period that ends
  // at the next chronological Fly In (the crew is off-site between rotations).
  const getRnRSegments = (person) => {
    const events = [];
    (person.fifo_dates || []).forEach(f => {
      if (f.fly_in_date) events.push({ type: 'in', date: new Date(f.fly_in_date) });
      if (f.fly_out_date) events.push({ type: 'out', date: new Date(f.fly_out_date) });
    });
    events.sort((a, b) => a.date - b.date);
    const segs = [];
    for (let i = 0; i < events.length; i++) {
      if (events[i].type === 'out') {
        const nextIn = events.slice(i + 1).find(e => e.type === 'in');
        if (nextIn && nextIn.date > events[i].date) segs.push({ start: events[i].date, end: nextIn.date });
      }
    }
    return segs;
  };

  const getProjectById = (id) => projects.find(p => p.id === id);

  const getPersonAllocations = (personId) => {
    return allocations.filter(a => a.personnel_id === personId && a.status === 'active');
  };

  // Assign each allocation to a "lane" so overlapping bars stack vertically
  const assignLanes = (allocs) => {
    const sorted = [...allocs].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    const lanes = [];
    return sorted.map(a => {
      const start = new Date(a.start_date);
      const end = new Date(a.end_date);
      let laneIdx = lanes.findIndex(le => le <= start);
      if (laneIdx === -1) {
        laneIdx = lanes.length;
        lanes.push(end);
      } else {
        lanes[laneIdx] = end;
      }
      return { ...a, _lane: laneIdx };
    });
  };

  const allocDurationDays = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
    return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
  };

  const getProjectColor = (status) => {
    if (status === 'Secured') return 'bg-emerald-500';
    if (status === 'Likely') return 'bg-amber-500';
    if (status === 'Prospect') return 'bg-blue-500';
    return 'bg-slate-500';
  };

  const getProjectBarStyle = (project) => {
    if (project?.label_color) {
      if (project.status === 'Secured') return { background: project.label_color };
      return { background: `repeating-linear-gradient(135deg, ${project.label_color}, ${project.label_color} 4px, ${project.label_color}80 4px, ${project.label_color}80 8px)` };
    }
    if (project?.status === 'Secured') return {};
    const color = project?.status === 'Likely' ? 'rgba(245, 158, 11, 0.85)' : 'rgba(59, 130, 246, 0.85)';
    const stripe = project?.status === 'Likely' ? 'rgba(245, 158, 11, 0.5)' : 'rgba(59, 130, 246, 0.5)';
    return { background: `repeating-linear-gradient(135deg, ${color}, ${color} 4px, ${stripe} 4px, ${stripe} 8px)` };
  };

  const navigateTime = (direction) => {
    const newDate = new Date(currentDate);
    if (timeScale === 'daily') newDate.setDate(newDate.getDate() + (direction * 7));
    else if (timeScale === 'weekly') newDate.setMonth(newDate.getMonth() + direction);
    else newDate.setMonth(newDate.getMonth() + (direction * 3));
    setCurrentDate(newDate);
  };
  // Keep the wheel handler's navigateTime reference current without re-attaching the listener.
  navigateTimeRef.current = navigateTime;

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => setIsFullScreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  // Slide handlers (drag middle of bar)
  const handleSlideStart = (e, allocationId) => {
    if (!canEdit) return;
    if (resizing) return;
    e.stopPropagation(); e.preventDefault();
    const alloc = allocations.find(a => a.id === allocationId);
    if (!alloc) return;
    setDidSlide(false);
    setSliding({ allocationId, initialX: e.clientX, initialStart: new Date(alloc.start_date), initialEnd: new Date(alloc.end_date) });
  };
  const handleSlideMove = (e) => {
    if (!sliding || !ganttAreaRef.current) return;
    const rect = ganttAreaRef.current.getBoundingClientRect();
    const adjustedEnd = getAdjustedTimelineEnd();
    const totalDays = (adjustedEnd - timelineStart) / 86400000;
    const daysPerPixel = totalDays / rect.width;
    const daysMoved = Math.round((e.clientX - sliding.initialX) * daysPerPixel);
    if (daysMoved === 0) return;
    setDidSlide(true);
    setAllocations(prev => prev.map(a => {
      if (a.id !== sliding.allocationId) return a;
      const ns = new Date(sliding.initialStart); ns.setDate(ns.getDate() + daysMoved);
      const ne = new Date(sliding.initialEnd); ne.setDate(ne.getDate() + daysMoved);
      return { ...a, start_date: ns.toISOString(), end_date: ne.toISOString() };
    }));
  };
  const handleSlideEnd = async () => {
    if (!sliding) return;
    const alloc = allocations.find(a => a.id === sliding.allocationId);
    if (alloc && didSlide) {
      try {
        await axios.put(`${API}/allocations/personnel/${alloc.id}`, {
          personnel_id: alloc.personnel_id, project_id: alloc.project_id,
          role_required: alloc.role_required,
          start_date: alloc.start_date, end_date: alloc.end_date,
          status: alloc.status, version: alloc.version || null
        }, { headers: getAuthHeader() });
        notification.success('Allocation moved');
        fetchData();
      } catch { notification.error('Failed to move'); fetchData(); }
    }
    setSliding(null); setTimeout(() => setDidSlide(false), 100);
  };

  // Resize handlers
  const handleResizeStart = (e, allocationId, edge, currentDateVal) => {
    if (sliding) return;
    e.stopPropagation(); e.preventDefault();
    setDidResize(false);
    setResizing({ allocationId, edge, initialX: e.clientX, initialDate: new Date(currentDateVal) });
  };
  const handleResizeMove = (e) => {
    if (!resizing || !ganttAreaRef.current) return;
    const rect = ganttAreaRef.current.getBoundingClientRect();
    const adjustedEnd = getAdjustedTimelineEnd();
    const totalDays = (adjustedEnd - timelineStart) / 86400000;
    const daysPerPixel = totalDays / rect.width;
    const daysMoved = Math.round((e.clientX - resizing.initialX) * daysPerPixel);
    if (daysMoved === 0) return;
    setDidResize(true);
    setAllocations(prev => prev.map(a => {
      if (a.id !== resizing.allocationId) return a;
      const newDate = new Date(resizing.initialDate); newDate.setDate(newDate.getDate() + daysMoved);
      if (resizing.edge === 'start') return { ...a, start_date: newDate.toISOString() };
      return { ...a, end_date: newDate.toISOString() };
    }));
  };
  const handleResizeEnd = async () => {
    if (!resizing) return;
    const alloc = allocations.find(a => a.id === resizing.allocationId);
    if (alloc && didResize) {
      try {
        await axios.put(`${API}/allocations/personnel/${alloc.id}`, {
          personnel_id: alloc.personnel_id, project_id: alloc.project_id,
          role_required: alloc.role_required,
          start_date: alloc.start_date, end_date: alloc.end_date,
          status: alloc.status, version: alloc.version || null
        }, { headers: getAuthHeader() });
        notification.success('Allocation resized');
        fetchData();
      } catch { notification.error('Failed to resize'); fetchData(); }
    }
    setResizing(null); setTimeout(() => setDidResize(false), 100);
  };

  // Mouse event listeners
  useEffect(() => {
    if (resizing || sliding) {
      const onMove = (e) => { if (resizing) handleResizeMove(e); if (sliding) handleSlideMove(e); };
      const onUp = () => { if (resizing) handleResizeEnd(); if (sliding) handleSlideEnd(); };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    }
  }, [resizing, sliding, allocations, timelineStart, timeScale]);

  // Shift+scroll for timeline navigation is wired via the setGanttAreaNode ref callback.

  const daysUntil = (date) => {
    const today = new Date();
    const target = new Date(date);
    return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
  };

  // Filter personnel
  const filteredPersonnel = personnel.filter(p => {
    if (searchFilter) {
      const s = searchFilter.toLowerCase();
      const match = (p.name || '').toLowerCase().includes(s) ||
        (p.role || '').toLowerCase().includes(s) ||
        (p.department || '').toLowerCase().includes(s);
      if (!match) return false;
    }
    if (roleFilter !== 'all' && p.role !== roleFilter) return false;
    if (departmentFilter !== 'all' && p.department !== departmentFilter) return false;
    // Only Allocated filter — must have at least one active allocation matching status + project filters
    if (onlyAllocated) {
      const allocs = getPersonAllocations(p.id).filter(a => {
        if (projectFilter !== 'all' && a.project_id !== projectFilter) return false;
        if (statusFilter === 'all') return true;
        const project = getProjectById(a.project_id);
        if (!project) return false;
        if (statusFilter === 'secured') return project.status === 'Secured';
        return project.status === 'Likely' || project.status === 'Prospect';
      });
      if (allocs.length === 0) return false;
    }
    return true;
  });

  // Group by role
  const groupedPersonnel = filteredPersonnel.reduce((acc, p) => {
    const role = p.role || 'Unassigned';
    if (!acc[role]) acc[role] = [];
    acc[role].push(p);
    return acc;
  }, {});

  // Filter allocations by status filter + specific project filter
  const getFilteredAllocations = (personId) => {
    let allocs = getPersonAllocations(personId);
    if (projectFilter !== 'all') {
      allocs = allocs.filter(a => a.project_id === projectFilter);
    }
    if (statusFilter === 'all') return allocs;
    return allocs.filter(a => {
      const project = getProjectById(a.project_id);
      if (!project) return false;
      if (statusFilter === 'secured') return project.status === 'Secured';
      return project.status === 'Likely' || project.status === 'Prospect';
    });
  };

  const totalFilteredAllocations = filteredPersonnel.reduce((sum, p) => sum + getFilteredAllocations(p.id).length, 0);

  const uniqueRoles = useMemo(() => [...new Set(personnel.map(p => p.role).filter(Boolean))].sort(), [personnel]);
  const uniqueDepartments = useMemo(() => [...new Set(personnel.map(p => p.department).filter(Boolean))].sort(), [personnel]);

  // Set of allocation IDs that have conflicts (for highlighting bars)
  const conflictingAllocIds = useMemo(() => {
    const s = new Set();
    conflicts.forEach(c => { s.add(c.allocation_1_id); s.add(c.allocation_2_id); });
    return s;
  }, [conflicts]);

  const toggleAllRoles = (collapse) => {
    const state = {};
    Object.keys(groupedPersonnel).forEach(role => { state[role] = collapse; });
    setCollapsedRoles(state);
  };

  // Quick delete allocation
  const handleDeleteAllocation = (allocId, e) => {
    e.stopPropagation();
    if (!canEdit) return;
    notification.confirm('Remove this allocation?', async () => {
      try {
        await axios.delete(`${API}/allocations/personnel/${allocId}`, { headers: getAuthHeader() });
        notification.success('Allocation removed');
        fetchData();
      } catch (error) {
        notification.error('Failed to remove allocation');
      }
    });
  };

  // Split allocation into two parts at a chosen date
  const [splitModal, setSplitModal] = useState(null);
  const handleSplitAllocation = async () => {
    if (!canEdit) return;
    if (!splitModal?.splitDate || !splitModal?.allocationId) return;
    const alloc = allocations.find(a => a.id === splitModal.allocationId);
    if (!alloc) return;
    const splitDate = new Date(splitModal.splitDate);
    const origStart = new Date(alloc.start_date);
    const origEnd = new Date(alloc.end_date);
    if (splitDate <= origStart || splitDate >= origEnd) {
      notification.error('Split date must be between start and end dates');
      return;
    }
    try {
      await axios.delete(`${API}/allocations/personnel/${alloc.id}`, { headers: getAuthHeader() });
      const firstEnd = new Date(splitDate);
      firstEnd.setDate(firstEnd.getDate() - 1);
      await axios.post(`${API}/allocations/personnel`, {
        personnel_id: alloc.personnel_id, project_id: alloc.project_id,
        role_required: alloc.role_required,
        start_date: origStart.toISOString(), end_date: firstEnd.toISOString(),
        status: alloc.status || 'active',
        is_override: !!alloc.is_override
      }, { headers: getAuthHeader() });
      await axios.post(`${API}/allocations/personnel`, {
        personnel_id: alloc.personnel_id, project_id: alloc.project_id,
        role_required: alloc.role_required,
        start_date: splitDate.toISOString(), end_date: origEnd.toISOString(),
        status: alloc.status || 'active',
        is_override: !!alloc.is_override
      }, { headers: getAuthHeader() });
      notification.success('Allocation split successfully');
      setSplitModal(null);
      fetchData();
    } catch (error) {
      notification.error(error.response?.data?.detail || 'Failed to split allocation');
    }
  };

  // Add allocation (supports single or batch)
  const handleAddAllocation = async (forceOverride = false) => {
    if (!canEdit) return;
    if (!allocModal?.projectId || !allocModal?.startDate || !allocModal?.endDate) {
      notification.error('Project, Start Date and End Date are required');
      return;
    }
    if (new Date(allocModal.endDate) <= new Date(allocModal.startDate)) {
      notification.error('End date must be after start date');
      return;
    }
    const isOverride = !!forceOverride || !!allocModal.isOverride;
    const ids = allocModal.personnelIds || [allocModal.personnelId];
    let created = 0;
    let failed = 0;
    for (const pId of ids) {
      const person = personnel.find(p => p.id === pId);
      try {
        await axios.post(`${API}/allocations/personnel`, {
          personnel_id: pId,
          project_id: allocModal.projectId,
          role_required: (allocModal.personnelIds ? person?.role : allocModal.roleRequired) || person?.role || null,
          start_date: new Date(allocModal.startDate).toISOString(),
          end_date: new Date(allocModal.endDate).toISOString(),
          status: 'active',
          is_override: isOverride,
          version: null
        }, { headers: getAuthHeader() });
        created++;
      } catch (error) {
        failed++;
        console.warn('Allocation skip', pId, error.response?.data?.detail);
      }
    }
    if (created > 0) {
      notification.success(`${created} allocation${created > 1 ? 's' : ''} created${isOverride ? ' (Override)' : ''}${failed > 0 ? ` • ${failed} skipped` : ''}`);
    } else {
      notification.error('Failed to create allocations');
    }
    setAllocModal(null);
    setSelectedIds([]);
    fetchData();
  };

  // Detect role mismatch when project is selected
  const allocRoleMismatch = useMemo(() => {
    if (!allocModal?.projectId) return null;
    const project = projects.find(p => p.id === allocModal.projectId);
    if (!project) return null;
    const ids = allocModal.personnelIds || [allocModal.personnelId];
    const forecasts = project.personnel_forecasts || [];
    if (forecasts.length === 0) {
      return `No personnel requirements defined for "${project.name}".`;
    }
    const mismatched = ids.filter(id => {
      const p = personnel.find(p => p.id === id);
      if (!p) return false;
      return !forecasts.find(f => f.role === p.role);
    });
    if (mismatched.length === 0) return null;
    if (ids.length === 1) {
      const p = personnel.find(p => p.id === mismatched[0]);
      return `"${project.name}" requires: ${forecasts.map(f => f.role).join(', ')}. This person's role is "${p?.role}".`;
    }
    return `${mismatched.length} of ${ids.length} selected personnel do not match the project's forecast roles (${forecasts.map(f => f.role).join(', ')}).`;
  }, [allocModal?.projectId, allocModal?.personnelId, allocModal?.personnelIds, projects, personnel]);

  // Detect dates that fall outside the project's start/end window
  const allocDateBoundsMismatch = useMemo(() => {
    if (!allocModal?.projectId || !allocModal?.startDate || !allocModal?.endDate) return null;
    const project = projects.find(p => p.id === allocModal.projectId);
    if (!project) return null;
    const projStart = new Date(project.start_date);
    const projEnd = new Date(project.end_date);
    const allocStart = new Date(allocModal.startDate);
    const allocEnd = new Date(allocModal.endDate);
    projStart.setHours(0, 0, 0, 0);
    projEnd.setHours(0, 0, 0, 0);
    allocStart.setHours(0, 0, 0, 0);
    allocEnd.setHours(0, 0, 0, 0);
    const startBefore = allocStart < projStart;
    const endAfter = allocEnd > projEnd;
    if (!startBefore && !endAfter) return null;
    const fmt = (d) => d.toLocaleDateString('en-AU');
    const parts = [];
    if (startBefore) parts.push(`Start ${fmt(allocStart)} is before project start ${fmt(projStart)}`);
    if (endAfter) parts.push(`End ${fmt(allocEnd)} is after project end ${fmt(projEnd)}`);
    return `${parts.join(' • ')}. Project "${project.name}" runs ${fmt(projStart)} → ${fmt(projEnd)}.`;
  }, [allocModal?.projectId, allocModal?.startDate, allocModal?.endDate, projects]);

  // Combined override warning (role mismatch OR date out-of-bounds)
  const allocOverrideWarning = allocRoleMismatch || allocDateBoundsMismatch;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-full p-0"
        ref={containerRef}
      >
        <div className={`h-full flex flex-col ${isFullScreen ? 'bg-slate-50' : ''}`}>
          {/* Header */}
          <div className="p-4 border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <SheetTitle className="text-xl font-bold tracking-tight text-slate-900">Personnel Overview</SheetTitle>
                <SheetDescription className="text-sm text-slate-500">
                  View allocations, rotations, and compliance for all personnel
                </SheetDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex border border-slate-300 rounded-md overflow-hidden">
                  <Button variant="ghost" size="sm" onClick={() => setTimeScale('daily')}
                    className={`rounded-none px-3 h-8 text-xs ${timeScale === 'daily' ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                    data-testid="pers-gantt-day-btn">Day</Button>
                  <Button variant="ghost" size="sm" onClick={() => setTimeScale('weekly')}
                    className={`rounded-none px-3 h-8 text-xs ${timeScale === 'weekly' ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                    data-testid="pers-gantt-week-btn">Week</Button>
                  <Button variant="ghost" size="sm" onClick={() => setTimeScale('monthly')}
                    className={`rounded-none px-3 h-8 text-xs ${timeScale === 'monthly' ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                    data-testid="pers-gantt-month-btn">Month</Button>
                </div>
                <Button variant="outline" size="sm" onClick={toggleFullScreen} className="h-8" data-testid="pers-gantt-fullscreen-btn">
                  {isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Search, Filters, and Navigation */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search personnel..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-8 h-8 text-sm"
                  data-testid="pers-gantt-search"
                />
              </div>

              {/* Status Filter (Secured/Forecasted) */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                data-testid="pers-gantt-status-filter"
              >
                <option value="all">All Statuses</option>
                <option value="secured">Secured Only</option>
                <option value="forecasted">Forecasted Only</option>
              </select>

              {/* Specific Project Filter */}
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs min-w-[160px] max-w-[240px]"
                data-testid="pers-gantt-project-filter"
                title="Filter by a specific project"
              >
                <option value="all">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              {/* Role Filter */}
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                data-testid="pers-gantt-role-filter"
              >
                <option value="all">All Roles</option>
                {uniqueRoles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>

              {/* Department Filter */}
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                data-testid="pers-gantt-department-filter"
              >
                <option value="all">All Departments</option>
                {uniqueDepartments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>

              {/* Only Allocated toggle */}
              <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none cursor-pointer h-8 px-2 border border-slate-300 rounded-md bg-white hover:bg-slate-50" title="Show only personnel with active allocations">
                <input
                  type="checkbox"
                  checked={onlyAllocated}
                  onChange={(e) => setOnlyAllocated(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="pers-gantt-only-allocated"
                />
                <span>Only Allocated</span>
              </label>

              {/* Show Visa Row toggle */}
              <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none cursor-pointer h-8 px-2 border border-slate-300 rounded-md bg-white hover:bg-slate-50" title="Show each person's visa validity timeline as a second row">
                <input
                  type="checkbox"
                  checked={showVisaRow}
                  onChange={(e) => setShowVisaRow(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="pers-gantt-show-visa-row"
                />
                <CreditCard className="w-3 h-3" />
                <span>Show Visa Row</span>
              </label>

              <div className="flex items-center gap-2 ml-auto">
                <Button variant="outline" size="sm" onClick={() => navigateTime(-1)} className="h-8 w-8 p-0" data-testid="pers-gantt-prev-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} className="h-8 px-3 text-xs" data-testid="pers-gantt-today-btn">
                  Today
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigateTime(1)} className="h-8 w-8 p-0" data-testid="pers-gantt-next-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-4 text-xs flex-wrap">
            <span className="font-semibold text-slate-600">Legend:</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-emerald-500 rounded"></span> Secured</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 rounded" style={{ background: 'repeating-linear-gradient(135deg, rgba(245,158,11,0.85), rgba(245,158,11,0.85) 3px, rgba(245,158,11,0.5) 3px, rgba(245,158,11,0.5) 6px)' }}></span> Likely</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 rounded" style={{ background: 'repeating-linear-gradient(135deg, rgba(59,130,246,0.85), rgba(59,130,246,0.85) 3px, rgba(59,130,246,0.5) 3px, rgba(59,130,246,0.5) 6px)' }}></span> Prospect</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-blue-500 rounded"></span> Rostered On</span>
            <span className="flex items-center gap-1"><span className="w-4 h-3 bg-slate-300 rounded border border-slate-400"></span> R&R</span>
            <span className="border-l border-slate-300 pl-4 flex items-center gap-1"><CreditCard className="w-3 h-3 text-orange-500" /> Visa</span>
            <span className="flex items-center gap-1"><Award className="w-3 h-3 text-red-500" /> Cert</span>
            <span className="flex items-center gap-1"><Stethoscope className="w-3 h-3 text-purple-500" /> Medical</span>
            <span className="flex items-center gap-1"><Plane className="w-3 h-3 text-emerald-600 -rotate-45" /> Fly In</span>
            <span className="flex items-center gap-1"><Plane className="w-3 h-3 text-blue-600 rotate-45" /> Fly Out</span>
            <span className="border-l border-slate-300 pl-4 text-slate-400">Drag bars to move • Drag edges to resize</span>
          </div>

          {/* Conflict Alert Banner */}
          {conflicts.length > 0 && (
            <div className="mx-4 mt-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" data-testid="pers-gantt-conflict-banner">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
              <span className="text-sm text-red-700 font-medium">
                {conflicts.length} personnel double-booking{conflicts.length > 1 ? 's' : ''} detected — overlapping project allocations found
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConflictsModal(true)}
                className="ml-auto h-7 text-xs border-red-300 text-red-700 hover:bg-red-100"
                data-testid="pers-gantt-view-conflicts-btn"
              >
                View Conflicts
              </Button>
            </div>
          )}

          {/* Visa Expiry Alert Banner — personnel with visas expiring within 30 days (incl. already expired) */}
          {(() => {
            const today = new Date();
            const upcoming = personnel
              .map(p => ({ person: p, visa: getCurrentVisa(p) }))
              .filter(x => {
                if (!x.visa?.expiry_date) return false;
                const days = Math.ceil((new Date(x.visa.expiry_date) - today) / (1000 * 60 * 60 * 24));
                return days <= 30; // includes expired (negative days)
              })
              .sort((a, b) => new Date(a.visa.expiry_date) - new Date(b.visa.expiry_date));
            if (upcoming.length === 0) return null;
            const expiredCount = upcoming.filter(x => new Date(x.visa.expiry_date) < today).length;
            return (
              <>
                <div
                  className="mx-4 mt-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2"
                  data-testid="pers-gantt-visa-alert-banner"
                >
                  <CreditCard className="w-4 h-4 text-amber-600 shrink-0" />
                  <span className="text-sm text-amber-800 font-medium">
                    {upcoming.length} personnel visa{upcoming.length > 1 ? 's' : ''} expiring within 30 days
                    {expiredCount > 0 && ` (${expiredCount} already expired)`}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowVisaAlertModal(true)}
                    className="ml-auto h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                    data-testid="pers-gantt-view-visa-alerts-btn"
                  >
                    View Visa Alerts
                  </Button>
                </div>
                {showVisaAlertModal && (
                  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setShowVisaAlertModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pers-gantt-visa-alert-modal">
                      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                        <div>
                          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-amber-600" />
                            Visa Expiry Alerts
                          </h3>
                          <p className="text-sm text-slate-500">{upcoming.length} personnel expiring within 30 days</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setShowVisaAlertModal(false)} className="h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
                      </div>
                      <div className="overflow-y-auto px-5 py-3">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                              <th className="py-2">Name</th>
                              <th className="py-2">Role</th>
                              <th className="py-2">Visa Type</th>
                              <th className="py-2">Expiry</th>
                              <th className="py-2">Days</th>
                            </tr>
                          </thead>
                          <tbody>
                            {upcoming.map(({ person, visa }) => {
                              const days = Math.ceil((new Date(visa.expiry_date) - today) / (1000 * 60 * 60 * 24));
                              const isExpired = days < 0;
                              return (
                                <tr key={person.id} className="border-b border-slate-100">
                                  <td className="py-2 font-medium text-slate-800">{person.name}</td>
                                  <td className="py-2 text-slate-600">{person.role || '-'}</td>
                                  <td className="py-2 text-slate-600">{visa.visa_type || 'Visa'}</td>
                                  <td className="py-2 text-slate-600">{formatDisplayDate(visa.expiry_date)}</td>
                                  <td className="py-2">
                                    <Badge className={isExpired ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                                      {isExpired ? `${Math.abs(days)}d ago` : `${days}d`}
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* Timeline Header */}
          <div className="flex border-b border-slate-200 bg-white">
            <div className="w-72 flex-shrink-0 px-3 py-2 border-r border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300"
                    data-testid="pers-gantt-select-all"
                    checked={filteredPersonnel.length > 0 && selectedIds.length === filteredPersonnel.length}
                    ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < filteredPersonnel.length; }}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedIds(filteredPersonnel.map(p => p.id));
                      else setSelectedIds([]);
                    }}
                    title="Select all visible personnel"
                  />
                  <span className="text-xs font-semibold text-slate-600 uppercase">Personnel</span>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => toggleAllRoles(false)} className="text-[9px] text-slate-500 hover:text-slate-700 underline" data-testid="pers-gantt-expand-all">Expand</button>
                  <span className="text-[9px] text-slate-300">|</span>
                  <button type="button" onClick={() => toggleAllRoles(true)} className="text-[9px] text-slate-500 hover:text-slate-700 underline" data-testid="pers-gantt-collapse-all">Collapse</button>
                </div>
              </div>
            </div>
            <div className="flex-1 flex">
              {timeline.map((t, idx) => (
                <div key={idx} className={`flex-1 text-center border-l border-slate-200 first:border-l-0 py-1 ${t.isWeekend ? 'bg-slate-100' : ''}`}>
                  <div className="text-xs font-medium text-slate-700">{t.label}</div>
                  <div className="text-[10px] text-slate-400">{t.subLabel}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Gantt Content */}
          <ScrollArea className="flex-1">
            <div ref={setGanttAreaNode} className={`min-w-[900px] ${resizing || sliding ? 'select-none' : ''}`}>
              {Object.keys(groupedPersonnel).length === 0 ? (
                <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
                  No personnel found matching the current filters
                </div>
              ) : (
                Object.entries(groupedPersonnel).map(([role, people]) => (
                  <div key={role}>
                    {/* Role Header - Collapsible */}
                    <div className="flex bg-slate-100 border-b border-slate-200 cursor-pointer" onClick={() => setCollapsedRoles(prev => ({ ...prev, [role]: !prev[role] }))}>
                      <div className="w-72 flex-shrink-0 px-3 py-1.5 border-r border-slate-200 flex items-center gap-1">
                        {collapsedRoles[role] ? <ChevronRight className="w-3 h-3 text-slate-500" /> : <ChevronDown className="w-3 h-3 text-slate-500" />}
                        <span className="text-xs font-semibold text-slate-700">{role}</span>
                        <Badge variant="secondary" className="ml-2 text-[10px]">{people.length}</Badge>
                      </div>
                      <div className="flex-1"></div>
                    </div>

                    {/* Personnel Rows - hidden when collapsed */}
                    {!collapsedRoles[role] && people.map((person) => {
                      const personAllocs = getFilteredAllocations(person.id);

                      return (
                        <div key={person.id}>
                        <div className={`flex border-b border-slate-100 hover:bg-slate-50 group ${selectedIds.includes(person.id) ? 'bg-emerald-50/40' : ''}`}>
                          {/* Name Column */}
                          <div className="w-72 flex-shrink-0 px-3 py-2 border-r border-slate-200">
                            <div className="flex items-center justify-between gap-2">
                              <input
                                type="checkbox"
                                className="rounded border-slate-300 flex-shrink-0"
                                data-testid={`pers-gantt-select-${person.id}`}
                                checked={selectedIds.includes(person.id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedIds(prev => [...prev, person.id]);
                                  else setSelectedIds(prev => prev.filter(id => id !== person.id));
                                }}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-medium text-slate-800 truncate">{person.name}</span>
                                  {person.rotation_roster && (
                                    <span className="text-[10px] text-slate-500">({person.rotation_roster.split('(')[0].trim()})</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {person.department && (
                                    <span className="text-[10px] text-slate-500 font-medium">{person.department}</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {/* Add Allocation button */}
                                {canEdit && (
                                <button
                                  type="button"
                                  onClick={() => setAllocModal({ personnelId: person.id, personnelName: person.name, projectId: '', roleRequired: person.role || '', startDate: formatDateForInput(new Date()), endDate: '' })}
                                  className="h-5 w-5 flex items-center justify-center rounded bg-slate-200 hover:bg-emerald-500 hover:text-white text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Add allocation"
                                  data-testid={`pers-gantt-add-alloc-${person.id}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                                )}
                                {/* Compliance warnings */}
                                {person.visa_expiry && daysUntil(person.visa_expiry) <= 90 && (
                                  <CreditCard className={`w-3 h-3 ${daysUntil(person.visa_expiry) <= 30 ? 'text-red-500' : 'text-orange-500'}`} title={`Visa expires ${formatDisplayDate(person.visa_expiry)}`} />
                                )}
                                {(person.certifications || []).some(c => c.expiry_date && daysUntil(c.expiry_date) <= 60) && (
                                  <Award className="w-3 h-3 text-red-500" title="Certification expiring soon" />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Gantt Bar Area */}
                          {(() => {
                            const lanedAllocs = assignLanes(personAllocs);
                            const numLanes = Math.max(1, ...(lanedAllocs.map(a => a._lane + 1)));
                            const laneHeight = 28;
                            const lanePad = 4;
                            const rowMinHeight = Math.max(40, numLanes * laneHeight + lanePad);
                            return (
                          <div className="flex-1 relative py-1" style={{ minHeight: `${rowMinHeight}px` }}>
                            {/* Timeline grid */}
                            <div className="absolute inset-0 flex">
                              {timeline.map((t, idx) => (
                                <div key={idx} className={`flex-1 border-l border-slate-100 first:border-l-0 ${t.isWeekend ? 'bg-slate-50' : ''}`} />
                              ))}
                            </div>

                            {/* FIFO Flight markers */}
                            {getFifoMarkers(person).map((fifoMarker, idx) => {
                              const markerStyle = getBarStyle(fifoMarker.date, fifoMarker.date);
                              if (!markerStyle) return null;
                              return (
                                <div
                                  key={`fifo-${idx}`}
                                  className={`absolute top-0.5 w-5 h-5 ${fifoMarker.color} rounded-full flex items-center justify-center z-40 shadow-md border-2 border-white cursor-pointer`}
                                  style={{ left: markerStyle.left }}
                                  title={`${fifoMarker.label}${fifoMarker.from && fifoMarker.to ? `\n${fifoMarker.from} → ${fifoMarker.to}` : ''}${fifoMarker.time ? `\n${fifoMarker.time}` : ''}`}
                                >
                                  <Plane className={`w-3 h-3 text-white ${fifoMarker.rotation}`} />
                                </div>
                              );
                            })}

                            {/* Allocation bars */}
                            {lanedAllocs.length === 0 ? (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-[10px] text-slate-400">No active allocations</span>
                              </div>
                            ) : (
                              lanedAllocs.map((alloc) => {
                                const project = getProjectById(alloc.project_id);
                                const barStyle = getBarStyle(alloc.start_date, alloc.end_date);
                                if (!barStyle) return null;

                                const rotationSegments = generateRotationSegments(alloc, person);
                                const markers = getComplianceMarkers(person, alloc);
                                const projectColor = getProjectColor(project?.status);
                                const barInlineStyle = getProjectBarStyle(project);
                                const hasRotation = rotationSegments.length > 0;
                                const isSlidingThis = sliding?.allocationId === alloc.id;
                                const isResizingThis = resizing?.allocationId === alloc.id;
                                const isConflict = conflictingAllocIds.has(alloc.id);
                                const durationDays = allocDurationDays(alloc.start_date, alloc.end_date);
                                const topPx = lanePad + (alloc._lane * laneHeight);

                                return (
                                  <div
                                    key={alloc.id}
                                    className="absolute h-7"
                                    style={{ ...barStyle, top: `${topPx}px` }}
                                  >
                                    <div
                                      className={`absolute inset-0 ${projectColor} rounded group/bar ${isSlidingThis || isResizingThis ? 'ring-2 ring-blue-400' : isConflict ? 'ring-2 ring-red-500 ring-offset-1' : 'hover:opacity-95'} ${sliding ? 'cursor-grabbing' : 'cursor-grab'}`}
                                      style={hasRotation ? {} : barInlineStyle}
                                      onMouseDown={(e) => {
                                        if (!e.target.closest('[data-resize-handle]')) handleSlideStart(e, alloc.id);
                                      }}
                                      title={`${person.name} → ${project?.name || 'Unknown'}: ${formatDisplayDate(alloc.start_date)} - ${formatDisplayDate(alloc.end_date)} • ${durationDays} day${durationDays === 1 ? '' : 's'}\nDrag to slide • Drag edges to resize`}
                                    >
                                      {/* Rotation segments overlay (replaces base color when rotation is set) */}
                                      {hasRotation && (
                                        <div className="absolute inset-0 rounded overflow-hidden">
                                          {/* base layer (project color, faded) */}
                                          <div className={`absolute inset-0 ${projectColor} opacity-40 rounded`} style={barInlineStyle}></div>
                                          {rotationSegments.map((seg, idx) => {
                                            const allocStart = new Date(alloc.start_date);
                                            const allocEnd = new Date(alloc.end_date);
                                            const adjustedEnd = getAdjustedTimelineEnd();
                                            const visibleStart = new Date(Math.max(allocStart.getTime(), timelineStart.getTime()));
                                            const visibleEnd = new Date(Math.min(allocEnd.getTime(), adjustedEnd.getTime()));
                                            const visibleDays = (visibleEnd - visibleStart) / (1000 * 60 * 60 * 24);
                                            if (visibleDays <= 0) return null;
                                            const segStartDays = Math.max(0, (seg.start - visibleStart) / (1000 * 60 * 60 * 24));
                                            const segEndDays = Math.min(visibleDays, (seg.end - visibleStart) / (1000 * 60 * 60 * 24));
                                            if (segEndDays <= 0 || segStartDays >= visibleDays) return null;
                                            const segLeft = (segStartDays / visibleDays) * 100;
                                            const segWidth = ((segEndDays - segStartDays) / visibleDays) * 100;
                                            const segmentColor = seg.type === 'rostered_on'
                                              ? 'bg-blue-500'
                                              : 'bg-slate-300 border border-slate-400';
                                            return (
                                              <div
                                                key={idx}
                                                className={`absolute h-full ${segmentColor} ${idx === 0 ? 'rounded-l' : ''} ${idx === rotationSegments.length - 1 ? 'rounded-r' : ''}`}
                                                style={{ left: `${Math.max(0, segLeft)}%`, width: `${Math.max(1, Math.min(100 - segLeft, segWidth))}%` }}
                                              />
                                            );
                                          })}
                                        </div>
                                      )}

                                      {/* R&R overlay derived from FIFO flights (Fly Out → next Fly In) */}
                                      {getRnRSegments(person).map((seg, idx) => {
                                        const allocStart = new Date(alloc.start_date);
                                        const allocEnd = new Date(alloc.end_date);
                                        const adjustedEnd = getAdjustedTimelineEnd();
                                        const visibleStart = new Date(Math.max(allocStart.getTime(), timelineStart.getTime()));
                                        const visibleEnd = new Date(Math.min(allocEnd.getTime(), adjustedEnd.getTime()));
                                        const visibleDays = (visibleEnd - visibleStart) / (1000 * 60 * 60 * 24);
                                        if (visibleDays <= 0) return null;
                                        const segStartDays = Math.max(0, (seg.start - visibleStart) / (1000 * 60 * 60 * 24));
                                        const segEndDays = Math.min(visibleDays, (seg.end - visibleStart) / (1000 * 60 * 60 * 24));
                                        if (segEndDays <= 0 || segStartDays >= visibleDays) return null;
                                        const segLeft = (segStartDays / visibleDays) * 100;
                                        const segWidth = ((segEndDays - segStartDays) / visibleDays) * 100;
                                        return (
                                          <div
                                            key={`rnr-${idx}`}
                                            className="absolute top-0 h-full bg-slate-300/85 border-x border-slate-400 z-[5] pointer-events-none flex items-center justify-center rounded-sm"
                                            style={{ left: `${Math.max(0, segLeft)}%`, width: `${Math.max(0.5, Math.min(100 - segLeft, segWidth))}%` }}
                                            data-testid={`pers-gantt-rnr-${alloc.id}-${idx}`}
                                            title={`R&R · ${formatDisplayDate(seg.start)} → ${formatDisplayDate(seg.end)}`}
                                          >
                                            {segWidth > 4 && <span className="text-[8px] font-semibold text-slate-600 uppercase tracking-wide">R&R</span>}
                                          </div>
                                        );
                                      })}
                                      {canEdit && (
                                      <div
                                        data-resize-handle="true"
                                        className="absolute left-0 top-0 w-3 h-full cursor-ew-resize bg-white/40 opacity-0 group-hover/bar:opacity-100 rounded-l transition-opacity z-20 hover:bg-white/60"
                                        onMouseDown={(e) => handleResizeStart(e, alloc.id, 'start', alloc.start_date)}
                                      />
                                      )}

                                      {/* Project name label */}
                                      <div className="absolute inset-0 flex items-center px-4 pointer-events-none z-10 gap-1.5">
                                        <span className="text-[10px] font-medium truncate drop-shadow-sm text-white">
                                          {project?.name || 'Unknown Project'}
                                        </span>
                                        <span className="text-[9px] text-white/80 font-mono whitespace-nowrap drop-shadow-sm" data-testid={`pers-gantt-bar-duration-${alloc.id}`}>
                                          • {durationDays}d
                                        </span>
                                      </div>

                                      {/* Compliance markers */}
                                      {markers.map((marker, idx) => {
                                        const allocStart = new Date(alloc.start_date);
                                        const allocEnd = new Date(alloc.end_date);
                                        const adjustedEnd = getAdjustedTimelineEnd();
                                        const visibleStart = new Date(Math.max(allocStart.getTime(), timelineStart.getTime()));
                                        const visibleEnd = new Date(Math.min(allocEnd.getTime(), adjustedEnd.getTime()));
                                        if (marker.date < visibleStart || marker.date > visibleEnd) return null;
                                        const visibleDays = (visibleEnd - visibleStart) / (1000 * 60 * 60 * 24);
                                        const markerDays = (marker.date - visibleStart) / (1000 * 60 * 60 * 24);
                                        const markerPos = (markerDays / visibleDays) * 100;
                                        if (markerPos < 0 || markerPos > 100 || visibleDays <= 0) return null;
                                        const MarkerIcon = marker.icon;
                                        return (
                                          <div
                                            key={idx}
                                            className={`absolute top-0 w-6 h-full ${marker.color} flex items-center justify-center rounded-sm z-30 shadow-md border border-white pointer-events-none`}
                                            style={{ left: `${Math.max(0, Math.min(94, markerPos))}%` }}
                                            title={`${marker.label}: ${formatDisplayDate(marker.date)}`}
                                          >
                                            <MarkerIcon className="w-3.5 h-3.5 text-white" />
                                          </div>
                                        );
                                      })}

                                      {/* Split button */}
                                      {canEdit && (
                                      <button
                                        data-resize-handle="true"
                                        className="absolute top-0 left-1/2 -translate-x-1/2 h-full w-5 flex items-center justify-center opacity-0 group-hover/bar:opacity-100 transition-opacity z-30 hover:bg-white/30 rounded"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const mid = new Date((new Date(alloc.start_date).getTime() + new Date(alloc.end_date).getTime()) / 2);
                                          setSplitModal({ allocationId: alloc.id, personName: person.name, projectName: project?.name, splitDate: formatDateForInput(mid) });
                                        }}
                                        title="Split allocation"
                                        data-testid={`pers-gantt-split-bar-${alloc.id}`}
                                      >
                                        <Scissors className="w-3 h-3 text-white drop-shadow" />
                                      </button>
                                      )}

                                      {/* Delete button */}
                                      {canEdit && (
                                      <button
                                        data-resize-handle="true"
                                        className="absolute top-0 right-4 h-full w-5 flex items-center justify-center opacity-0 group-hover/bar:opacity-100 transition-opacity z-30 hover:bg-red-500/50 rounded"
                                        onClick={(e) => handleDeleteAllocation(alloc.id, e)}
                                        title="Remove allocation"
                                      >
                                        <X className="w-3 h-3 text-white drop-shadow" />
                                      </button>
                                      )}

                                      {/* Right resize handle */}
                                      {canEdit && (
                                      <div
                                        data-resize-handle="true"
                                        className="absolute right-0 top-0 w-3 h-full cursor-ew-resize bg-white/40 opacity-0 group-hover/bar:opacity-100 rounded-r transition-opacity z-20 hover:bg-white/60"
                                        onMouseDown={(e) => handleResizeStart(e, alloc.id, 'end', alloc.end_date)}
                                      />
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                            );
                          })()}
                        </div>
                        {/* Visa Rows - one row per visa so concurrent visas show side-by-side */}
                        {showVisaRow && (() => {
                          const visas = getAllVisas(person);
                          if (visas.length === 0) {
                            return (
                              <div className="flex border-b border-slate-100 bg-slate-50/40" data-testid={`pers-gantt-visa-row-${person.id}`}>
                                <div className="w-72 flex-shrink-0 px-3 py-1 border-r border-slate-200 flex items-center gap-2 pl-8">
                                  <CreditCard className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                  <span className="text-[11px] text-slate-400 italic truncate">No visa on file</span>
                                </div>
                                <div className="flex-1 relative" style={{ minHeight: '24px' }}>
                                  <div className="absolute inset-0 flex">
                                    {timeline.map((t, idx) => (
                                      <div key={idx} className={`flex-1 border-l border-slate-100 first:border-l-0 ${t.isWeekend ? 'bg-slate-100/40' : ''}`} />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return visas.map((visa, vIdx) => {
                            const visaStart = visa?.start_date ? new Date(visa.start_date) : null;
                            const visaEnd = visa?.expiry_date ? new Date(visa.expiry_date) : null;
                            let barStyle = null;
                            if (visaStart && visaEnd && visaEnd >= visaStart) {
                              barStyle = getBarStyle(visaStart, visaEnd);
                            }
                            const barColor = getVisaBarColor(visa?.expiry_date);
                            const visaLabel = [visa.visa_type || 'Visa', visa.country].filter(Boolean).join(' • ');
                            return (
                              <div key={visa.id || vIdx} className="flex border-b border-slate-100 bg-slate-50/40" data-testid={`pers-gantt-visa-row-${person.id}-${vIdx}`}>
                                <div className="w-72 flex-shrink-0 px-3 py-1 border-r border-slate-200 flex items-center gap-2 pl-8">
                                  <CreditCard className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <span className="text-[11px] text-slate-500 truncate">
                                      {visa.visa_type || 'Visa'}{visa.country ? ` • ${visa.country}` : ''}
                                    </span>
                                    {visa?.expiry_date && (
                                      <span className="text-[10px] text-slate-400 ml-1.5">exp {formatDisplayDate(visa.expiry_date)}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex-1 relative" style={{ minHeight: '24px' }}>
                                  <div className="absolute inset-0 flex">
                                    {timeline.map((t, idx) => (
                                      <div key={idx} className={`flex-1 border-l border-slate-100 first:border-l-0 ${t.isWeekend ? 'bg-slate-100/40' : ''}`} />
                                    ))}
                                  </div>
                                  {barStyle ? (
                                    <div
                                      className={`absolute top-1 h-4 rounded ${barColor} flex items-center px-1.5 shadow-sm`}
                                      style={{ left: barStyle.left, width: barStyle.width }}
                                      title={`${visaLabel} • ${visa.start_date ? formatDisplayDate(visa.start_date) : '—'} → ${formatDisplayDate(visa.expiry_date)}`}
                                    >
                                      <span className="text-[9px] text-white font-medium truncate">{visaLabel}</span>
                                    </div>
                                  ) : (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <span className="text-[10px] text-slate-400 italic">
                                        {visa && visa.expiry_date && !visa.start_date
                                          ? `${visa.visa_type || 'Visa'} expires ${formatDisplayDate(visa.expiry_date)} (no start date on file)`
                                          : 'No visa dates on file'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center justify-between" data-testid="pers-gantt-footer">
            <span>{filteredPersonnel.length} personnel • {totalFilteredAllocations} active allocations{projectFilter !== 'all' ? ` • filtered to ${projects.find(p => p.id === projectFilter)?.name || 'selected project'}` : ''}</span>
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2" data-testid="pers-gantt-selection-bar">
                <span className="text-xs font-medium text-emerald-700">{selectedIds.length} selected</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedIds([])}
                  className="h-7 text-xs"
                  data-testid="pers-gantt-clear-selection"
                >
                  Clear
                </Button>
                {canEdit && (<>
                <Button
                  size="sm"
                  onClick={() => {
                    const targetAllocs = allocations.filter(a =>
                      a.status === 'active' &&
                      selectedIds.includes(a.personnel_id) &&
                      (projectFilter === 'all' || a.project_id === projectFilter)
                    );
                    if (targetAllocs.length === 0) {
                      notification.error('No active allocations found for the selected personnel' + (projectFilter !== 'all' ? ' in the filtered project' : ''));
                      return;
                    }
                    const projName = projectFilter !== 'all' ? projects.find(p => p.id === projectFilter)?.name : null;
                    notification.confirm(
                      `Delete ${targetAllocs.length} allocation${targetAllocs.length > 1 ? 's' : ''} across ${selectedIds.length} personnel${projName ? ` from "${projName}"` : ''}?\n\nThis cannot be undone.`,
                      async () => {
                        let deleted = 0;
                        for (const a of targetAllocs) {
                          try {
                            await axios.delete(`${API}/allocations/personnel/${a.id}`, { headers: getAuthHeader() });
                            deleted++;
                          } catch { /* skip */ }
                        }
                        notification.success(`${deleted} allocation${deleted !== 1 ? 's' : ''} removed`);
                        setSelectedIds([]);
                        fetchData();
                      },
                      { title: 'Delete Allocations', confirmLabel: `Delete ${targetAllocs.length}`, confirmClass: 'bg-red-600 hover:bg-red-700' }
                    );
                  }}
                  variant="outline"
                  className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="pers-gantt-delete-selected"
                >
                  Delete Selected
                </Button>
                <Button
                  size="sm"
                  onClick={() => setAllocModal({
                    personnelIds: [...selectedIds],
                    count: selectedIds.length,
                    projectId: '',
                    startDate: formatDateForInput(new Date()),
                    endDate: ''
                  })}
                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="pers-gantt-allocate-selected"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Allocate Selected ({selectedIds.length})
                </Button>
                </>)}
              </div>
            )}
          </div>

          {/* Conflicts Modal - inside SheetContent to stay within Radix focus scope */}
          {showConflictsModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setShowConflictsModal(false)}>
              <div className="bg-white rounded-lg shadow-xl w-[700px] max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pers-gantt-conflicts-modal">
                <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Personnel Double-Bookings</h3>
                    <p className="text-sm text-slate-500">{conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} detected</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setShowConflictsModal(false)} className="h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                  {conflicts.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No conflicts detected</p>
                  ) : (
                    <div className="space-y-3">
                      {conflicts.map((c, idx) => {
                        const person = personnel.find(p => p.id === c.resource_id);
                        const proj1 = projects.find(p => p.id === c.allocation_1_project);
                        const proj2 = projects.find(p => p.id === c.allocation_2_project);
                        return (
                          <div key={idx} className="border border-red-200 rounded-lg p-4 bg-red-50/50">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                              <span className="font-semibold text-slate-800">{person?.name || c.resource_id.slice(0, 8)}</span>
                              {person && <span className="text-xs text-slate-500">{person.role}{person.department ? ` • ${person.department}` : ''}</span>}
                            </div>
                            <div className="grid grid-cols-2 gap-3 ml-6">
                              <div className="bg-white rounded p-2.5 border border-slate-200">
                                <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">Allocation 1</div>
                                <div className="text-sm font-medium text-slate-800">{proj1?.name || 'Unknown'}</div>
                              </div>
                              <div className="bg-white rounded p-2.5 border border-slate-200">
                                <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1">Allocation 2</div>
                                <div className="text-sm font-medium text-slate-800">{proj2?.name || 'Unknown'}</div>
                              </div>
                            </div>
                            <div className="ml-6 mt-2 flex items-center gap-2 text-xs text-red-700 bg-red-100 rounded px-2.5 py-1.5">
                              <Clock className="w-3.5 h-3.5" />
                              <span>Overlap: <strong>{formatDisplayDate(c.overlap_start)}</strong> — <strong>{formatDisplayDate(c.overlap_end)}</strong></span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Add Allocation Modal - inside SheetContent to stay within Radix focus scope */}
          {allocModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => setAllocModal(null)}>
              <div className="bg-white rounded-lg shadow-2xl p-6 w-96 border border-slate-200" onClick={(e) => e.stopPropagation()} data-testid="pers-gantt-alloc-modal">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{allocModal.personnelIds ? 'Batch Add Allocation' : 'Add Allocation'}</h3>
                <p className="text-sm text-slate-500 mb-4">
                  {allocModal.personnelIds
                    ? <>Allocate <span className="font-medium text-slate-700">{allocModal.count} personnel</span> to a project</>
                    : <>Allocate <span className="font-medium text-slate-700">{allocModal.personnelName}</span> to a project</>}
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Project *</Label>
                    <select
                      value={allocModal.projectId}
                      onChange={(e) => {
                        const proj = projects.find(p => p.id === e.target.value);
                        setAllocModal({ ...allocModal, projectId: e.target.value, endDate: proj ? formatDateForInput(proj.end_date) : allocModal.endDate });
                      }}
                      className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-1"
                      data-testid="pers-gantt-alloc-project"
                    >
                      <option value="">Select Project</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.status})</option>)}
                    </select>
                  </div>
                  {!allocModal.personnelIds && (
                    <div>
                      <Label className="text-sm font-semibold text-slate-700">Role Required</Label>
                      <Input
                        value={allocModal.roleRequired || ''}
                        onChange={(e) => setAllocModal({ ...allocModal, roleRequired: e.target.value })}
                        placeholder="e.g. Crane Operator"
                        className="mt-1"
                        data-testid="pers-gantt-alloc-role"
                      />
                    </div>
                  )}
                  {allocModal.personnelIds && (
                    <div className="text-xs text-slate-500 italic">Each person will be allocated using their own role. Role override is not available in batch mode.</div>
                  )}
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Start Date *</Label>
                    <Input type="date" value={allocModal.startDate} onChange={(e) => setAllocModal({ ...allocModal, startDate: e.target.value })} className="mt-1" data-testid="pers-gantt-alloc-start" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">End Date *</Label>
                    <Input type="date" value={allocModal.endDate} onChange={(e) => setAllocModal({ ...allocModal, endDate: e.target.value })} className="mt-1" data-testid="pers-gantt-alloc-end" />
                  </div>
                  {allocOverrideWarning && (
                    <div className="rounded-md border border-orange-300 bg-orange-50 p-2.5 text-xs text-orange-800 flex items-start gap-2" data-testid="pers-gantt-alloc-override-warning">
                      <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold mb-0.5">Override required</div>
                        <div className="leading-snug">{allocOverrideWarning}</div>
                        <div className="mt-1 text-[10px] text-orange-700">Click <strong>Override</strong> to proceed — this allocation will be flagged as an Override.</div>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setAllocModal(null)}>Cancel</Button>
                    {allocOverrideWarning ? (
                      <Button size="sm" onClick={() => handleAddAllocation(true)} data-testid="pers-gantt-alloc-override-btn" className="bg-orange-500 hover:bg-orange-600">
                        <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />Override
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => handleAddAllocation(false)} data-testid="pers-gantt-alloc-confirm" className="bg-slate-900 hover:bg-slate-800">
                        <Plus className="w-3.5 h-3.5 mr-1.5" />Allocate
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Split allocation modal */}
          {splitModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={() => setSplitModal(null)}>
              <div className="bg-white rounded-lg shadow-xl p-6 w-96" onClick={(e) => e.stopPropagation()} data-testid="pers-gantt-split-modal">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Split Allocation</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Split <span className="font-medium text-slate-700">{splitModal.personName}</span> on <span className="font-medium text-slate-700">{splitModal.projectName}</span> into two parts
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Split at date</Label>
                    <Input
                      type="date"
                      value={splitModal.splitDate}
                      onChange={(e) => setSplitModal({ ...splitModal, splitDate: e.target.value })}
                      data-testid="pers-gantt-split-date-input"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">First half ends day before, second half starts on this date</p>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setSplitModal(null)}>Cancel</Button>
                    <Button size="sm" onClick={handleSplitAllocation} data-testid="pers-gantt-confirm-split-button" className="bg-slate-900 hover:bg-slate-800">
                      <Scissors className="w-3.5 h-3.5 mr-1.5" />Split
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
